import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Calendar,
  Globe,
  Shield,
  CheckCircle,
  LogOut,
  AlertCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Video,
  User,
  Clock,
  CalendarClock,
  CalendarCheck,
  Plus,
  Trash2,
} from 'lucide-react'
import { interviewerApi, type MyAvailability } from '../../lib/api/interviewer'
import {
  schedulerApi,
  type SchedulerConfig,
  type WeeklyRule,
  type DateOverride,
} from '../../lib/api/scheduler'
import { formatCalendarDateUS } from '../../lib/utils/timezone'

// ── Calendar constants ────────────────────────────────────────────────────────
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Add `n` days to today and return a "YYYY-MM-DD" string (local). Used to bound
// the availability-extension date picker (min today+46, max today+60).
const addDaysISO = (n: number): string => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── Native Scheduler ("My Availability" tab) constants & helpers ───────────────
const FULL_DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// Buffer values the backend accepts.
const BUFFER_OPTIONS = [0, 30, 60, 120] as const
type BufferMinutes = (typeof BUFFER_OPTIONS)[number]

// Reasonable IANA timezone list; America/New_York is the platform default.
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern Time — America/New_York' },
  { value: 'America/Chicago', label: 'Central Time — America/Chicago' },
  { value: 'America/Denver', label: 'Mountain Time — America/Denver' },
  { value: 'America/Phoenix', label: 'Mountain (no DST) — America/Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — America/Los_Angeles' },
  { value: 'America/Anchorage', label: 'Alaska Time — America/Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time — Pacific/Honolulu' },
  { value: 'Europe/London', label: 'GMT / BST — Europe/London' },
  { value: 'Europe/Paris', label: 'Central European — Europe/Paris' },
  { value: 'Europe/Berlin', label: 'Central European — Europe/Berlin' },
  { value: 'Asia/Dubai', label: 'Gulf — Asia/Dubai' },
  { value: 'Asia/Kolkata', label: 'India — Asia/Kolkata' },
  { value: 'Asia/Singapore', label: 'Singapore — Asia/Singapore' },
  { value: 'Asia/Tokyo', label: 'Japan — Asia/Tokyo' },
  { value: 'Australia/Sydney', label: 'Australian Eastern — Australia/Sydney' },
  { value: 'UTC', label: 'UTC' },
]

// A single editable weekly time window (start/end in "HH:MM", 24h).
interface TimeWindow {
  start: string
  end: string
}

// "HH:MM" strings are zero-padded 24h, so lexical comparison equals chronological.
// Validates each day's windows: both ends filled, end > start, and no overlaps.
// Returns a human-readable error string, or null when valid.
function validateWeeklyRows(rows: TimeWindow[][]): string | null {
  for (let day = 0; day < 7; day++) {
    const windows = rows[day] ?? []
    for (const w of windows) {
      if (!w.start || !w.end) return `${FULL_DAY_LABELS[day]}: fill in both start and end times.`
      if (w.end <= w.start) return `${FULL_DAY_LABELS[day]}: end time must be after start time.`
    }
    const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start))
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].start < sorted[i - 1].end) {
        return `${FULL_DAY_LABELS[day]}: time windows overlap.`
      }
    }
  }
  return null
}
const HOUR_HEIGHT = 64           // px per 1 hour
const DAY_START_HOUR = 0         // 12:00 AM
const DAY_END_HOUR = 24          // 12:00 AM (next day)
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR  // 24
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT       // 1536px

// Build hour labels: 12 AM … 11 PM
const HOUR_LABELS = Array.from({ length: TOTAL_HOURS }, (_, i) => {
  const h = DAY_START_HOUR + i
  if (h === 0) return '12 AM'
  if (h === 12) return '12 PM'
  return h < 12 ? `${h} AM` : `${h - 12} PM`
})

interface BookingItem {
  booking_id: number
  session_id: number | null
  status: string
  scheduled_start_utc: string
  scheduled_end_utc: string
  duration_minutes: number
  client_name: string | null
  client_email: string
  zoom_join_url: string | null
  zoom_host_url: string | null
  zoom_password: string | null
}

interface Toast {
  type: 'success' | 'error' | 'warning'
  text: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getWeekDates(offset: number): Date[] {
  const now = new Date()
  const dayOfWeek = now.getDay() // 0 = Sun
  const monday = new Date(now)
  monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7) + offset * 7)
  monday.setHours(0, 0, 0, 0)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return d
  })
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

function bookingHeightPx(durationMinutes: number): number {
  return Math.max(24, (durationMinutes / 60) * HOUR_HEIGHT)
}

function formatLastSynced(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000)
  if (secs < 10) return 'just now'
  if (secs < 60) return `${secs}s ago`
  const mins = Math.floor(secs / 60)
  return `${mins}m ago`
}

function fmtTime(iso: string, tz?: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit',
    ...(tz ? { timeZone: tz } : {}),
  })
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function isSameDayInTZ(utcIso: string, calendarDate: Date, tz?: string): boolean {
  if (!tz) return isSameDay(new Date(utcIso), calendarDate)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(new Date(utcIso))
  const year  = parseInt(parts.find(p => p.type === 'year')!.value)
  const month = parseInt(parts.find(p => p.type === 'month')!.value) - 1
  const day   = parseInt(parts.find(p => p.type === 'day')!.value)
  return year === calendarDate.getFullYear() && month === calendarDate.getMonth() && day === calendarDate.getDate()
}

function bookingTopPxTZ(startUtc: string, tz?: string): number {
  let hours: number
  if (tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false,
    }).formatToParts(new Date(startUtc))
    const h = parseInt(parts.find(p => p.type === 'hour')!.value)
    const m = parseInt(parts.find(p => p.type === 'minute')!.value)
    hours = (h === 24 ? 0 : h) + m / 60
  } else {
    const d = new Date(startUtc)
    hours = d.getHours() + d.getMinutes() / 60
  }
  return Math.max(0, (hours - DAY_START_HOUR) * HOUR_HEIGHT)
}

const STATUS_STYLE: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  confirmed:       { bg: 'rgba(37,99,235,0.10)', border: '#2563eb', text: '#1d4ed8', dot: '#2563eb' },
  pending_payment: { bg: 'rgba(217,119,6,0.10)',  border: '#d97706', text: '#92400e', dot: '#d97706' },
  cancelled:       { bg: 'rgba(107,114,128,0.08)', border: '#9ca3af', text: '#6b7280', dot: '#9ca3af' },
  completed:       { bg: 'rgba(22,163,74,0.10)',  border: '#16a34a', text: '#15803d', dot: '#16a34a' },
}

export default function InterviewerSchedule() {
  const navigate = useNavigate()
  void navigate // kept for future sub-route navigation
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('schedule')

  // ── Weekly calendar state ──────────────────────────────────────────────────
  const [bookings, setBookings] = useState<BookingItem[]>([])
  const [weekOffset, setWeekOffset] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const weekDates = getWeekDates(weekOffset)

  // ── Available Slots tab state ──────────────────────────────────────────────
  const [avail, setAvail] = useState<MyAvailability | null>(null)
  const [availLoading, setAvailLoading] = useState(false)
  const [availWeekOffset, setAvailWeekOffset] = useState(0)

  // ── Booking detail modal state (read-only — interviewers raise a change
  //    request from the dashboard instead of cancelling here) ────────────────
  const [selectedBooking, setSelectedBooking] = useState<BookingItem | null>(null)

  // ── Zoom state ─────────────────────────────────────────────────────────────
  const [zoomConnected, setZoomConnected] = useState(false)
  const [zoomUserEmail, setZoomUserEmail] = useState<string | null>(null)
  const [zoomConnectedAt, setZoomConnectedAt] = useState<string | null>(null)
  const [zoomLoading, setZoomLoading] = useState(true)
  const [zoomConnecting, setZoomConnecting] = useState(false)
  const [zoomDisconnecting, setZoomDisconnecting] = useState(false)
  const [showZoomPlanModal, setShowZoomPlanModal] = useState(false)

  // Toast notification
  const [toast, setToast] = useState<Toast | null>(null)

  // ── Native Scheduler "My Availability" tab ─────────────────────────────────
  const queryClient = useQueryClient()
  const myAvailActive = activeTab === 'my-availability'

  // Config is fetched eagerly (not gated on the active tab) so the "My
  // Availability" tab can show an accepting-bookings indicator dot even while
  // another tab is open. Rules/overrides stay lazy.
  const configQuery = useQuery({
    queryKey: ['scheduler', 'config'],
    queryFn: schedulerApi.getConfig,
  })
  const rulesQuery = useQuery({
    queryKey: ['scheduler', 'rules'],
    queryFn: schedulerApi.getRules,
    enabled: myAvailActive,
  })
  const overridesQuery = useQuery({
    queryKey: ['scheduler', 'overrides'],
    queryFn: schedulerApi.getOverrides,
    enabled: myAvailActive,
  })
  // Current effective booking window + latest availability-extension request.
  const extensionQuery = useQuery({
    queryKey: ['scheduler', 'extension-status'],
    queryFn: schedulerApi.getMyExtensionStatus,
    enabled: myAvailActive,
  })

  // Editable config form state.
  const [cfgTimezone, setCfgTimezone] = useState('America/New_York')
  const [cfgBufferBefore, setCfgBufferBefore] = useState<BufferMinutes>(0)
  const [cfgBufferAfter, setCfgBufferAfter] = useState<BufferMinutes>(0)
  const [cfgAccepting, setCfgAccepting] = useState(true)
  const [cfgAdminLocked, setCfgAdminLocked] = useState(false)
  // Preserve fields the UI does not edit (min notice / window / slot duration)
  // so a config save never silently resets them.
  const [cfgSnapshot, setCfgSnapshot] = useState<SchedulerConfig | null>(null)

  // Editable weekly rules: one array of windows per weekday (index 0 = Mon).
  const [weeklyRows, setWeeklyRows] = useState<TimeWindow[][]>(() =>
    Array.from({ length: 7 }, () => [] as TimeWindow[]),
  )

  // Date-override form state.
  const [ovDate, setOvDate] = useState('')
  const [ovType, setOvType] = useState<'custom' | 'blocked'>('custom')
  const [ovStart, setOvStart] = useState('09:00')
  const [ovEnd, setOvEnd] = useState('17:00')

  // Availability-extension request modal state.
  const [showExtModal, setShowExtModal] = useState(false)
  // A day COUNT, not a date: the grant rolls forward with the calendar.
  const [extExtraDays, setExtExtraDays] = useState(5)
  const [showCancelExtModal, setShowCancelExtModal] = useState(false)
  // Which state we are backing out of — changes the wording and the warning.
  const [cancelExtKind, setCancelExtKind] = useState<'pending' | 'approved'>('approved')
  const [extReason, setExtReason] = useState('')

  // Seed the config form whenever fresh config data arrives.
  useEffect(() => {
    const cfg = configQuery.data
    if (!cfg) return
    setCfgTimezone(cfg.timezone || 'America/New_York')
    setCfgBufferBefore((BUFFER_OPTIONS.includes(cfg.buffer_before_minutes as BufferMinutes) ? cfg.buffer_before_minutes : 0) as BufferMinutes)
    setCfgBufferAfter((BUFFER_OPTIONS.includes(cfg.buffer_after_minutes as BufferMinutes) ? cfg.buffer_after_minutes : 0) as BufferMinutes)
    setCfgAccepting(cfg.is_accepting_bookings !== false)
    setCfgAdminLocked(cfg.admin_locked === true)
    setCfgSnapshot(cfg)
  }, [configQuery.data])

  // Seed the weekly editor whenever fresh rules arrive.
  useEffect(() => {
    const rules = rulesQuery.data
    if (!rules) return
    const rows: TimeWindow[][] = Array.from({ length: 7 }, () => [] as TimeWindow[])
    for (const r of rules) {
      if (r.is_active === false) continue
      const d = r.day_of_week
      if (d >= 0 && d <= 6) {
        rows[d].push({ start: (r.start_time ?? '').slice(0, 5), end: (r.end_time ?? '').slice(0, 5) })
      }
    }
    rows.forEach((w) => w.sort((a, b) => a.start.localeCompare(b.start)))
    setWeeklyRows(rows)
  }, [rulesQuery.data])

  // Everything an availability write can move. The server now clears its slot
  // cache inside the write's own transaction, so a refetch right after the save
  // already returns the new slots — this just makes sure the client asks again
  // instead of rendering what it fetched before the edit.
  // Dropping `avail` is enough for the week grid: the save controls only exist
  // on the "My Availability" tab, so the "Available Slots" tab is always
  // re-entered afterwards and its effect refetches on entry.
  const refetchAfterAvailabilityWrite = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduler', 'month-availability'] })
    setAvail(null)
  }

  // Mutations.
  const saveConfigMut = useMutation({
    mutationFn: (body: Partial<SchedulerConfig>) => schedulerApi.updateConfig(body),
    onSuccess: (data) => {
      queryClient.setQueryData(['scheduler', 'config'], data)
      refetchAfterAvailabilityWrite()
      setToast({ type: 'success', text: 'Availability settings saved.' })
    },
    onError: (e) => setToast({ type: 'error', text: e instanceof Error ? e.message : 'Failed to save settings.' }),
  })

  // Request availability beyond the standard 45-day window (admin-approved ≤60).
  const requestExtensionMut = useMutation({
    mutationFn: (body: { extra_days: number; reason: string }) =>
      schedulerApi.requestExtension(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'extension-status'] })
      setShowExtModal(false)
      setExtReason('')
      setToast({ type: 'success', text: 'Extension request submitted for admin review.' })
    },
    onError: (e) => setToast({ type: 'error', text: e instanceof Error ? e.message : 'Failed to submit request.' }),
  })

  // Cancelling an extension shrinks the bookable window, so the slot cache and
  // the month view must be refetched alongside the extension status.
  const cancelExtensionMut = useMutation({
    mutationFn: () => schedulerApi.cancelMyExtension(),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'extension-status'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'config'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'month-availability'] })
      setShowCancelExtModal(false)
      setToast({
        type: 'success',
        text:
          cancelExtKind === 'pending'
            ? 'Extension request withdrawn.'
            : `Extended availability cancelled. Clients can book you up to the standard ${
                extensionQuery.data?.current_window_days ?? 45
              } days again.`,
      })
      void row
    },
    onError: (e) =>
      setToast({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to cancel the extension.',
      }),
  })

  // FR-IA-6 is WARN-BUT-ALLOW: these mutations succeed even when the change
  // leaves an already-booked session outside the new availability. On success we
  // show an amber warning toast carrying the server's message when present,
  // otherwise the normal success toast.
  const saveRulesMut = useMutation({
    mutationFn: (rules: WeeklyRule[]) => schedulerApi.updateRules(rules),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'rules'] })
      refetchAfterAvailabilityWrite()
      setToast(
        data?.warning
          ? { type: 'warning', text: data.warning }
          : { type: 'success', text: 'Weekly hours saved.' },
      )
    },
    onError: (e) => setToast({ type: 'error', text: e instanceof Error ? e.message : 'Failed to save weekly hours.' }),
  })

  const createOverrideMut = useMutation({
    mutationFn: (override: Omit<DateOverride, 'id'>) => schedulerApi.createOverride(override),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'overrides'] })
      refetchAfterAvailabilityWrite()
      setOvDate('')
      setToast(
        data?.warning
          ? { type: 'warning', text: data.warning }
          : { type: 'success', text: 'Date override added.' },
      )
    },
    onError: (e) => setToast({ type: 'error', text: e instanceof Error ? e.message : 'Failed to add override.' }),
  })

  const deleteOverrideMut = useMutation({
    mutationFn: (id: number) => schedulerApi.deleteOverride(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'overrides'] })
      refetchAfterAvailabilityWrite()
      setToast(
        data?.warning
          ? { type: 'warning', text: data.warning }
          : { type: 'success', text: 'Date override removed.' },
      )
    },
    onError: (e) => setToast({ type: 'error', text: e instanceof Error ? e.message : 'Failed to remove override.' }),
  })

  // Weekly editor row helpers.
  const addWindow = (day: number) =>
    setWeeklyRows((rows) => rows.map((w, i) => (i === day ? [...w, { start: '09:00', end: '17:00' }] : w)))
  const removeWindow = (day: number, idx: number) =>
    setWeeklyRows((rows) => rows.map((w, i) => (i === day ? w.filter((_, j) => j !== idx) : w)))
  const updateWindow = (day: number, idx: number, field: keyof TimeWindow, value: string) =>
    setWeeklyRows((rows) =>
      rows.map((w, i) => (i === day ? w.map((win, j) => (j === idx ? { ...win, [field]: value } : win)) : w)),
    )

  const handleSaveSchedulerConfig = () => {
    saveConfigMut.mutate({
      timezone: cfgTimezone,
      buffer_before_minutes: cfgBufferBefore,
      buffer_after_minutes: cfgBufferAfter,
      // is_accepting_bookings is deliberately NOT sent: it is owned by the
      // "Availability" tab toggle now. PUT /scheduler/config is a partial update
      // (exclude_unset), so omitting it means a timezone/buffer save can never
      // clobber a flag that was changed on the other tab.
      // Preserve non-edited fields from the last loaded config.
      min_notice_hours: cfgSnapshot?.min_notice_hours ?? 24,
      booking_window_days: cfgSnapshot?.booking_window_days ?? 45,
      slot_duration_minutes: cfgSnapshot?.slot_duration_minutes ?? null,
    })
  }

  const handleSaveWeeklyRules = () => {
    const err = validateWeeklyRows(weeklyRows)
    if (err) {
      setToast({ type: 'error', text: err })
      return
    }
    const rules: WeeklyRule[] = []
    weeklyRows.forEach((windows, day) => {
      windows.forEach((w) => rules.push({ day_of_week: day, start_time: w.start, end_time: w.end, is_active: true }))
    })
    saveRulesMut.mutate(rules)
  }

  const handleAddOverride = () => {
    if (!ovDate) {
      setToast({ type: 'error', text: 'Pick a date for the override.' })
      return
    }
    if (ovType === 'custom' && (!ovStart || !ovEnd || ovEnd <= ovStart)) {
      setToast({ type: 'error', text: 'Custom hours need an end time after the start time.' })
      return
    }
    createOverrideMut.mutate({
      date: ovDate,
      type: ovType,
      ...(ovType === 'custom' ? { start_time: ovStart, end_time: ovEnd } : {}),
    })
  }

  // Ensure the loaded timezone always appears as an option even if it's not in
  // the curated list.
  const timezoneOptions = TIMEZONE_OPTIONS.some((t) => t.value === cfgTimezone)
    ? TIMEZONE_OPTIONS
    : [{ value: cfgTimezone, label: cfgTimezone }, ...TIMEZONE_OPTIONS]

  // Availability toggle
  const [isAvailable, setIsAvailable] = useState(true)
  const [adminLocked, setAdminLocked] = useState(false)
  const [togglingAvailability, setTogglingAvailability] = useState(false)
  // Two independent admin locks guard this one toggle: interviewer_profiles
  // .admin_locked (admin user management) and sched_config.admin_locked (admin
  // scheduling console). The toggle writes both flags, so either lock blocks it —
  // show the locked banner rather than letting the save fail with a 400.
  const availabilityLocked = adminLocked || cfgAdminLocked

  // Preferences
  const [prefTimezone, setPrefTimezone] = useState('')
  const [prefLanguage, setPrefLanguage] = useState('')
  const [savedTimezone, setSavedTimezone] = useState('')
  const [savedLanguage, setSavedLanguage] = useState('')
  const [profileSnapshot, setProfileSnapshot] = useState<Record<string, unknown>>({})
  const [savingPreferences, setSavingPreferences] = useState(false)

  const preferencesChanged = prefTimezone !== savedTimezone || prefLanguage !== savedLanguage

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(t)
  }, [toast])

  // ── Read ?zoom= query param after OAuth redirect ──────────────────────────
  useEffect(() => {
    const zoomStatus = searchParams.get('zoom')
    if (zoomStatus) {
      if (zoomStatus === 'connected') {
        setToast({ type: 'success', text: 'Zoom account connected successfully!' })
        setActiveTab('zoom')
        loadZoomStatus()
      } else if (zoomStatus === 'plan_error') {
        setToast({ type: 'error', text: 'Your Zoom account is on a Free plan. Please upgrade to Zoom Pro, Business, or higher — cloud recording and transcripts require a paid plan.' })
        setActiveTab('zoom')
      } else if (zoomStatus === 'account_in_use') {
        setToast({ type: 'error', text: 'This Zoom account is already connected to another interviewer. Each interviewer must use their own Zoom account.' })
        setActiveTab('zoom')
      } else if (zoomStatus === 'error') {
        setToast({ type: 'error', text: 'Could not connect Zoom. Please try again.' })
        setActiveTab('zoom')
      }
      setSearchParams({}, { replace: true })
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadBookings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    try {
      const res = await interviewerApi.getMySchedule()
      const items: BookingItem[] = (res.bookings ?? res ?? []).filter(
        (b: BookingItem) => !['payment_expired', 'payment_failed'].includes(b.status)
      )
      setBookings(items)
      setLastSynced(new Date())
    } catch (err) {
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  // ── Load the interviewer's own open slots for a given week ─────────────────
  const loadMyAvailability = useCallback(async (offset: number) => {
    setAvailLoading(true)
    try {
      const monday = getWeekDates(offset)[0]
      const start = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`
      const data = await interviewerApi.getMyAvailability(start, 7)
      setAvail(data)
    } catch {
      setAvail(null)
    } finally {
      setAvailLoading(false)
    }
  }, [])

  // Fetch availability whenever the tab is opened or its week changes.
  useEffect(() => {
    if (activeTab === 'availability-slots') {
      loadMyAvailability(availWeekOffset)
    }
  }, [activeTab, availWeekOffset, loadMyAvailability])

  const loadZoomStatus = async () => {
    setZoomLoading(true)
    try {
      const status = await interviewerApi.getZoomStatus()
      setZoomConnected(status.is_connected)
      setZoomUserEmail(status.zoom_user_email ?? null)
      setZoomConnectedAt(status.connected_at ?? null)
    } catch {
      setZoomConnected(false)
      setZoomUserEmail(null)
      setZoomConnectedAt(null)
    } finally {
      setZoomLoading(false)
    }
  }

  const loadAvailability = async () => {
    try {
      const res = await interviewerApi.getSchedule().catch(() => null)
      if (res) {
        const profile = res.profile ?? res
        setIsAvailable(profile.is_available !== false)
        setAdminLocked(profile.admin_locked === true)
      }
    } catch { /* ignore */ }
  }

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadBookings()
    loadZoomStatus()
    loadAvailability()
    loadPreferences()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-poll every 30 s while schedule tab is active ─────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'schedule' && document.visibilityState === 'visible') {
        loadBookings(true)
      }
    }, 30_000)
    return () => clearInterval(interval)
  }, [activeTab, loadBookings])

  // ── Refetch when user returns to this browser tab ────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && activeTab === 'schedule') {
        loadBookings(true)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [activeTab, loadBookings])

  const handleToggleAvailability = async () => {
    const next = !isAvailable
    setTogglingAvailability(true)
    try {
      await interviewerApi.toggleAvailability(next)
      setIsAvailable(next)
      // The same call now flips sched_config.is_accepting_bookings server-side,
      // so refetch the scheduler config to keep the "My Availability" tab dot
      // (and its cached copy) honest.
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'config'] })
      setToast({
        type: 'success',
        text: next
          ? 'You are now available. Clients can book new sessions with you.'
          : 'You are now unavailable. Clients cannot book new sessions with you.',
      })
    } catch (err) {
      setToast({
        type: 'error',
        text: err instanceof Error ? err.message : 'Could not update your availability.',
      })
    } finally {
      setTogglingAvailability(false)
    }
  }

  const loadPreferences = async () => {
    try {
      const res = await interviewerApi.getProfile()
      const profile = res.data ?? res
      setPrefTimezone(profile.timezone ?? '')
      setPrefLanguage(profile.language ?? '')
      setSavedTimezone(profile.timezone ?? '')
      setSavedLanguage(profile.language ?? '')
      setProfileSnapshot({
        bio: profile.bio,
        specializations: profile.specializations,
        years_experience: profile.years_experience,
        avatar_url: profile.avatar_url,
      })
    } catch (err) {
    }
  }

  const handleSavePreferences = async () => {
    setSavingPreferences(true)
    try {
      await interviewerApi.updateProfile({
        ...profileSnapshot,
        timezone: prefTimezone,
        language: prefLanguage,
      })
      setSavedTimezone(prefTimezone)
      setSavedLanguage(prefLanguage)
      setToast({ type: 'success', text: 'Preferences saved.' })
    } catch {
      setToast({ type: 'error', text: 'Failed to save preferences. Please try again.' })
    } finally {
      setSavingPreferences(false)
    }
  }

  // ── Bookings for a given day ───────────────────────────────────────────────
  const tz = savedTimezone || undefined
  const bookingsForDay = (date: Date) =>
    bookings.filter((b) => isSameDayInTZ(b.scheduled_start_utc, date, tz))

  if (loading) {
    return <div className="loading-screen">Loading schedule...</div>
  }

  return (
    <div>
      {/* ── Toast notification ── */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 20px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            background:
              toast.type === 'error' ? '#fff5f5'
              : toast.type === 'warning' ? '#fffbeb'
              : '#f0fff4',
            color:
              toast.type === 'error' ? 'var(--danger)'
              : toast.type === 'warning' ? '#b45309'
              : 'var(--success)',
            border: `1px solid ${
              toast.type === 'error' ? 'rgba(229,62,62,0.25)'
              : toast.type === 'warning' ? 'rgba(217,119,6,0.30)'
              : 'rgba(72,187,120,0.25)'
            }`,
            maxWidth: 360,
            animation: 'slideIn 0.2s ease',
          }}
        >
          <style>{`
            @keyframes slideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
          {toast.type === 'success'
            ? <CheckCircle size={18} />
            : <AlertCircle size={18} />}
          {toast.text}
        </div>
      )}

      <div className="page-header">
        <h1 className="page-title">My Schedule</h1>
        <p className="page-subtitle">
          View your interview schedule, check your available slots, and manage your availability
        </p>
      </div>

      {/* Tabs */}
      <div className="is-tabs-wrap">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <Calendar size={16} />
            Weekly Schedule
          </button>
          <button
            className={`tab ${activeTab === 'availability-slots' ? 'active' : ''}`}
            onClick={() => setActiveTab('availability-slots')}
          >
            <CalendarClock size={16} />
            Available Slots
          </button>
          <button
            className={`tab ${activeTab === 'my-availability' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-availability')}
          >
            <CalendarCheck size={16} />
            My Availability
            {configQuery.data && cfgAccepting && (
              <span
                title="Accepting bookings"
                style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', display: 'inline-block', marginLeft: 5, flexShrink: 0 }}
              />
            )}
          </button>
          <button
            className={`tab ${activeTab === 'zoom' ? 'active' : ''}`}
            onClick={() => setActiveTab('zoom')}
          >
            <Video size={16} />
            Zoom Account
            {zoomConnected && (
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', display: 'inline-block', marginLeft: 5, flexShrink: 0 }} />
            )}
          </button>
          <button
            className={`tab ${activeTab === 'availability' ? 'active' : ''}`}
            onClick={() => setActiveTab('availability')}
          >
            <Globe size={16} />
            Availability
          </button>
          <button
            className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
            onClick={() => { setActiveTab('preferences'); loadPreferences() }}
          >
            <Settings size={16} />
            Preferences
          </button>
        </div>
      </div>

      {/* ── Weekly Schedule tab ── */}
      {activeTab === 'schedule' && (
        <>
          {/* toolbar */}
          <div className="is-toolbar">
            {/* week navigation + date — left group */}
            <div className="is-toolbar-left">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button className="is-nav-btn" onClick={() => setWeekOffset(w => w - 1)}>
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="is-nav-btn is-nav-today"
                  onClick={() => setWeekOffset(0)}
                  disabled={weekOffset === 0}
                >
                  Today
                </button>
                <button className="is-nav-btn" onClick={() => setWeekOffset(w => w + 1)}>
                  <ChevronRight size={16} />
                </button>
              </div>
              <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>
                {fmtDate(weekDates[0])} – {fmtDate(weekDates[6])}
              </span>
            </div>

            {/* sync — right group */}
            <div className="is-toolbar-right">
              {lastSynced && !refreshing && (
                <span className="is-sync-label">
                  <span className="is-sync-dot" />
                  Synced {formatLastSynced(lastSynced)}
                </span>
              )}
              <button
                className="is-nav-btn"
                onClick={() => loadBookings(true)}
                disabled={refreshing}
                title="Refresh"
              >
                <RefreshCw size={15} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
                {refreshing ? 'Syncing…' : 'Sync'}
              </button>
            </div>
          </div>

          {/* policy notice */}
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '12px 18px', background: 'rgba(45,55,72,0.03)' }}>
            <Shield size={16} style={{ color: 'var(--primary-navy)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Bookings appear here automatically as clients book. Click any booking to view its details.
            </span>
          </div>

          {/* calendar grid */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as 'auto' }}>
              <div style={{ minWidth: 680 }}>

                {/* header row: time col + 7 day cols */}
                <div className="is-cal-header-row">
                  <div className="is-time-col" />
                  {weekDates.map((date, i) => {
                    const isToday = isSameDay(date, new Date())
                    const dayBookings = bookingsForDay(date).filter(b => b.status !== 'cancelled')
                    return (
                      <div key={i} className={`is-day-header ${isToday ? 'is-today' : ''}`}>
                        <span className="is-day-label">{DAY_LABELS[i]}</span>
                        <span className={`is-day-date ${isToday ? 'is-today-date' : ''}`}>
                          {date.getDate()}
                        </span>
                        {dayBookings.length > 0 && (
                          <span className="is-day-count">{dayBookings.length}</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* body: time labels + day columns */}
                <div style={{ display: 'flex' }}>
                  {/* time labels */}
                  <div className="is-time-col" style={{ position: 'relative', height: GRID_HEIGHT }}>
                    {HOUR_LABELS.map((label, i) => (
                      <div
                        key={label}
                        style={{
                          position: 'absolute',
                          top: i * HOUR_HEIGHT - 8,
                          right: 8,
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* day columns */}
                  {weekDates.map((date, colIdx) => {
                    const isToday = isSameDay(date, new Date())
                    const dayBkgs = bookingsForDay(date)
                    return (
                      <div
                        key={colIdx}
                        className={`is-day-col ${isToday ? 'is-today-col' : ''}`}
                        style={{ height: GRID_HEIGHT }}
                      >
                        {/* hour grid lines */}
                        {HOUR_LABELS.map((_, i) => (
                          <div
                            key={i}
                            style={{
                              position: 'absolute',
                              top: i * HOUR_HEIGHT,
                              left: 0, right: 0,
                              borderTop: `1px solid ${i === 0 ? 'transparent' : 'var(--border)'}`,
                            }}
                          />
                        ))}

                        {/* booking cards */}
                        {dayBkgs.map((bk) => {
                          const top = bookingTopPxTZ(bk.scheduled_start_utc, tz)
                          const height = bookingHeightPx(bk.duration_minutes)
                          const style = STATUS_STYLE[bk.status] ?? STATUS_STYLE.confirmed
                          const isCancelled = bk.status === 'cancelled'
                          return (
                            <div
                              key={bk.booking_id}
                              className="is-booking-card"
                              style={{
                                top,
                                height,
                                background: style.bg,
                                borderLeft: `3px solid ${style.border}`,
                                opacity: isCancelled ? 0.55 : 1,
                                cursor: isCancelled ? 'default' : 'pointer',
                              }}
                              onClick={() => !isCancelled && setSelectedBooking(bk)}
                            >
                              <div className="is-bk-name">
                                {bk.client_name || bk.client_email.split('@')[0]}
                              </div>
                              <div className="is-bk-time" style={{ color: style.text }}>
                                {fmtTime(bk.scheduled_start_utc, tz)} · {bk.duration_minutes}m
                              </div>
                              {isCancelled && (
                                <div className="is-bk-cancelled">Cancelled</div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* empty state */}
            {bookings.filter(b =>
              weekDates.some(wd => isSameDayInTZ(b.scheduled_start_utc, wd, tz))
            ).length === 0 && (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                No bookings this week. Clients will appear here as they book sessions.
              </div>
            )}
          </div>

          {/* ── Booking detail modal ── */}
          {selectedBooking && (
            <div className="is-modal-overlay" onClick={() => setSelectedBooking(null)}>
              <div className="is-modal" onClick={(e) => e.stopPropagation()}>
                {/* header */}
                <div className="is-modal-header">
                  <div>
                    <h3 className="is-modal-title">Interview Session</h3>
                    <span
                      className="is-modal-status"
                      style={{
                        background: (STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).bg,
                        color: (STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).text,
                        border: `1px solid ${(STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).border}`,
                      }}
                    >
                      {selectedBooking.status.replace('_', ' ')}
                    </span>
                  </div>
                  <button className="is-modal-close" onClick={() => setSelectedBooking(null)}>
                    <X size={18} />
                  </button>
                </div>

                {/* detail rows */}
                <div className="is-modal-body">
                  <div className="is-modal-row">
                    <User size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div>
                      <div className="is-modal-label">Client</div>
                      <div className="is-modal-val">
                        {selectedBooking.client_name || '—'}
                        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>
                          {selectedBooking.client_email}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="is-modal-row">
                    <Clock size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div>
                      <div className="is-modal-label">Date & Time</div>
                      <div className="is-modal-val">
                        {new Date(selectedBooking.scheduled_start_utc).toLocaleDateString('en-US', {
                          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                          ...(tz ? { timeZone: tz } : {}),
                        })}
                        {' · '}{fmtTime(selectedBooking.scheduled_start_utc, tz)}
                        {' – '}{fmtTime(selectedBooking.scheduled_end_utc, tz)}
                        {' ('}{selectedBooking.duration_minutes} min{')'}
                        {tz && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>({tz})</span>}
                      </div>
                    </div>
                  </div>

                  {selectedBooking.zoom_host_url && (
                    <div className="is-modal-row">
                      <Video size={15} style={{ color: '#2563eb', flexShrink: 0 }} />
                      <div>
                        <div className="is-modal-label">Zoom (Host Link)</div>
                        <a
                          href={selectedBooking.zoom_host_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="is-modal-zoom-link"
                        >
                          Start Meeting as Host ↗
                        </a>
                        {selectedBooking.zoom_password && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                            Password: <strong style={{ color: 'var(--text-dark)' }}>{selectedBooking.zoom_password}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

            .is-sync-label {
              display: inline-flex; align-items: center; gap: 5px;
              font-size: 12px; color: var(--text-muted); font-weight: 500;
            }
            .is-sync-dot {
              width: 7px; height: 7px; border-radius: 50%; background: #16a34a;
              animation: pulse 2.4s ease-in-out infinite;
            }

            .is-nav-btn {
              display: inline-flex; align-items: center; gap: 5px;
              padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 500;
              background: white; border: 1px solid var(--border); color: var(--text-dark);
              cursor: pointer; transition: all 0.15s;
            }
            .is-nav-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
            .is-nav-btn:disabled { opacity: 0.45; cursor: default; }
            .is-nav-today { min-width: 64px; justify-content: center; }

            .is-cal-header-row {
              display: grid;
              grid-template-columns: 52px repeat(7, 1fr);
              border-bottom: 1px solid var(--border);
              background: #fafbfc;
            }
            .is-time-col { width: 52px; flex-shrink: 0; }
            .is-day-header {
              display: flex; flex-direction: column; align-items: center;
              padding: 10px 4px; gap: 4px; border-left: 1px solid var(--border);
            }
            .is-day-header.is-today { background: rgba(37,99,235,0.04); }
            .is-day-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
            .is-day-date {
              width: 28px; height: 28px; border-radius: 50%;
              display: flex; align-items: center; justify-content: center;
              font-size: 14px; font-weight: 600; color: var(--text-dark);
            }
            .is-today-date { background: var(--primary-navy, #1e2432); color: white !important; }
            .is-day-count {
              font-size: 10px; font-weight: 700; background: #2563eb; color: white;
              border-radius: 10px; padding: 1px 6px; min-width: 16px; text-align: center;
            }

            .is-day-col {
              position: relative; flex: 1;
              border-left: 1px solid var(--border);
              overflow: visible;
            }
            .is-today-col { background: rgba(37,99,235,0.02); }

            .is-booking-card {
              position: absolute; left: 3px; right: 3px;
              border-radius: 6px; padding: 5px 7px;
              overflow: hidden; transition: filter 0.15s;
              z-index: 1;
            }
            .is-booking-card:hover:not([style*="cursor: default"]) { filter: brightness(0.96); }
            .is-bk-name { font-size: 11.5px; font-weight: 700; color: var(--text-dark); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .is-bk-time { font-size: 10.5px; font-weight: 500; }
            .is-bk-cancelled { font-size: 10px; font-weight: 600; color: #9ca3af; margin-top: 2px; }

            /* modal */
            .is-modal-overlay {
              position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
              display: flex; align-items: center; justify-content: center; padding: 24px;
            }
            .is-modal {
              background: white; border-radius: 16px; width: 100%; max-width: 460px;
              box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
            }
            .is-modal-header {
              display: flex; align-items: flex-start; justify-content: space-between;
              padding: 20px 22px 16px; border-bottom: 1px solid var(--border);
            }
            .is-modal-title { font-size: 17px; font-weight: 700; color: var(--text-dark); margin: 0 0 6px; }
            .is-modal-status {
              display: inline-block; padding: 3px 10px; border-radius: 20px;
              font-size: 11.5px; font-weight: 600; text-transform: capitalize;
            }
            .is-modal-close {
              background: none; border: none; cursor: pointer; padding: 4px;
              color: var(--text-muted); border-radius: 6px; display: flex;
            }
            .is-modal-close:hover { background: #f3f4f6; }
            .is-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 16px; }
            .is-modal-row { display: flex; align-items: flex-start; gap: 12px; }
            .is-modal-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 3px; }
            .is-modal-val { font-size: 13.5px; font-weight: 500; color: var(--text-dark); }
            .is-modal-zoom-link { font-size: 13.5px; font-weight: 600; color: #2563eb; text-decoration: none; }
            .is-modal-zoom-link:hover { text-decoration: underline; }
            .is-modal-footer { padding: 16px 22px; border-top: 1px solid var(--border); background: #fafbfc; }
          `}</style>
        </>
      )}

      {/* ── Available Slots tab ── */}
      {activeTab === 'availability-slots' && (() => {
        const availTz = avail?.timezone || savedTimezone || undefined
        const availWeekDates = getWeekDates(availWeekOffset)
        // Mirrors the "Availability" tab toggle (which also drives
        // sched_config.is_accepting_bookings server-side — see
        // handleToggleAvailability). While unavailable, clients cannot book
        // any of these slots even though the underlying weekly-hours shape
        // is unchanged, so the pills are dimmed rather than hidden.
        const slotsAreLive = isAvailable

        // One row per day of the visible week — OPEN (bookable) slots only,
        // sorted by start time. Booked sessions are intentionally not shown here.
        const dayRows = (avail?.days ?? []).map((day) => {
          const slots = day.slots
            .map((s) => ({ startUtc: s.start_utc, label: fmtTime(s.start_utc, availTz) }))
            .sort((a, b) => a.startUtc.localeCompare(b.startUtc))
          return { date: day.date, openCount: slots.length, slots }
        })
        const totalOpen = dayRows.reduce((n, d) => n + d.openCount, 0)

        return (
          <>
            {/* toolbar */}
            <div className="is-toolbar">
              <div className="is-toolbar-left">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    className="is-nav-btn"
                    onClick={() => setAvailWeekOffset((w) => Math.max(0, w - 1))}
                    disabled={availWeekOffset === 0}
                    title={availWeekOffset === 0 ? 'Past weeks are not available' : 'Previous week'}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="is-nav-btn is-nav-today"
                    onClick={() => setAvailWeekOffset(0)}
                    disabled={availWeekOffset === 0}
                  >
                    This week
                  </button>
                  <button className="is-nav-btn" onClick={() => setAvailWeekOffset((w) => w + 1)} title="Next week">
                    <ChevronRight size={16} />
                  </button>
                </div>
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>
                  {fmtDate(availWeekDates[0])} – {fmtDate(availWeekDates[6])}
                </span>
              </div>
              <div className="is-toolbar-right">
                <button
                  className="is-nav-btn"
                  onClick={() => loadMyAvailability(availWeekOffset)}
                  disabled={availLoading}
                  title="Refresh"
                >
                  <RefreshCw size={15} style={{ animation: availLoading ? 'spin 0.8s linear infinite' : 'none' }} />
                  {availLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* context bar: timezone (availability is computed in this timezone) */}
            <div className="avs-context">
              <span className="avs-chip"><Globe size={13} />{availTz || 'UTC'}</span>
              <span className="avs-context-note">
                These are the exact slots clients can book — your weekly hours in your timezone, minus booked sessions and the buffer around them.
              </span>
            </div>

            {!slotsAreLive && (
              <div className="avs-unavailable-banner">
                <AlertCircle size={15} style={{ flexShrink: 0 }} />
                <span>
                  You're marked <strong>Not Available</strong> — clients can't book any of the slots below. This is
                  just your weekly hours shape; go to the{' '}
                  <button type="button" onClick={() => setActiveTab('availability')} className="avs-inline-link">
                    Availability
                  </button>{' '}
                  tab to start accepting bookings again.
                </span>
              </div>
            )}

            {availLoading && !avail ? (
              <div className="card" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                Loading your availability…
              </div>
            ) : avail && !avail.connected ? (
              <div className="card" style={{ padding: '40px 24px', textAlign: 'center' }}>
                <CalendarClock size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Set your availability to see your slots</div>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', maxWidth: 420, margin: '0 auto 16px' }}>
                  Your available slots come from your weekly hours. Set your weekly availability to see your bookable times here.
                </p>
                <button className="btn btn-primary" onClick={() => setActiveTab('my-availability')}>
                  Go to My Availability
                </button>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {totalOpen === 0 && (
                  <div style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, borderBottom: '1px solid var(--border)', background: 'rgba(45,55,72,0.02)' }}>
                    No open slots this week. Slots appear from your weekly hours once booked sessions and buffer are excluded.
                  </div>
                )}
                <div className="avs-list">
                  {dayRows.map((row) => {
                    const [y, mo, da] = row.date.split('-').map(Number)
                    const dateObj = new Date(y, mo - 1, da)
                    const isToday = isSameDay(dateObj, new Date())
                    return (
                      <div key={row.date} className={`avs-day ${isToday ? 'avs-day-today' : ''}`}>
                        <div className="avs-day-head">
                          <div className="avs-day-weekday">
                            {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                            {isToday && <span className="avs-today-badge">Today</span>}
                          </div>
                          <div className="avs-day-date">{dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                          <div className={`avs-day-count ${slotsAreLive ? '' : 'avs-day-count-inactive'}`}>{row.openCount} open</div>
                        </div>
                        <div className="avs-pills">
                          {row.slots.length === 0 ? (
                            <span className="avs-empty">No open slots</span>
                          ) : (
                            row.slots.map((s, i) => (
                              <span
                                key={i}
                                className={`avs-pill ${slotsAreLive ? 'avs-pill-open' : 'avs-pill-inactive'}`}
                                title={slotsAreLive ? 'Available to book' : "Not bookable — you're marked unavailable"}
                              >
                                {s.label}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <style>{`
              @keyframes spin { to { transform: rotate(360deg); } }
              .is-sync-label { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted); font-weight: 500; }
              .is-nav-btn {
                display: inline-flex; align-items: center; gap: 5px;
                padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 500;
                background: white; border: 1px solid var(--border); color: var(--text-dark);
                cursor: pointer; transition: all 0.15s;
              }
              .is-nav-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
              .is-nav-btn:disabled { opacity: 0.45; cursor: default; }
              .is-nav-today { min-width: 64px; justify-content: center; }

              /* context bar */
              .avs-context {
                display: flex; align-items: center; flex-wrap: wrap; gap: 10px 16px;
                margin-bottom: 16px; padding: 12px 16px; border-radius: 12px;
                background: rgba(45,55,72,0.03); border: 1px solid var(--border);
              }
              .avs-chip {
                display: inline-flex; align-items: center; gap: 6px;
                font-size: 12.5px; font-weight: 600; color: var(--text-dark);
                background: white; border: 1px solid var(--border);
                padding: 4px 10px; border-radius: 20px;
              }
              .avs-chip svg { color: var(--primary-navy, #1e2432); }
              .avs-context-note { font-size: 12.5px; color: var(--text-muted); line-height: 1.4; flex: 1; min-width: 220px; }

              /* unavailable banner */
              .avs-unavailable-banner {
                display: flex; align-items: flex-start; gap: 10px;
                margin-bottom: 16px; padding: 12px 16px; border-radius: 12px;
                background: rgba(229,62,62,0.06); border: 1px solid rgba(229,62,62,0.25);
                color: #9b2c2c; font-size: 13px; line-height: 1.5;
              }
              .avs-inline-link {
                background: none; border: none; padding: 0; font: inherit;
                color: #9b2c2c; font-weight: 700; cursor: pointer; text-decoration: underline;
              }

              /* day-sectioned pill list */
              .avs-list { display: flex; flex-direction: column; }
              .avs-day { display: flex; gap: 18px; padding: 16px 20px; border-bottom: 1px solid var(--border); }
              .avs-day:last-child { border-bottom: none; }
              .avs-day-today { background: rgba(37,99,235,0.035); }
              .avs-day-head { width: 96px; flex-shrink: 0; }
              .avs-day-weekday {
                font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
                color: var(--text-muted); display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
              }
              .avs-today-badge {
                font-size: 9px; font-weight: 700; background: var(--primary-navy, #1e2432); color: #fff;
                border-radius: 8px; padding: 1px 6px; letter-spacing: 0.3px;
              }
              .avs-day-date { font-size: 16px; font-weight: 700; color: var(--text-dark); margin-top: 3px; }
              .avs-day-count { font-size: 11px; color: #16a34a; font-weight: 600; margin-top: 2px; }
              .avs-day-count-inactive { color: var(--text-muted); }
              .avs-pills { display: flex; flex-wrap: wrap; gap: 8px; align-content: flex-start; flex: 1; }
              .avs-pill {
                display: inline-flex; align-items: center; gap: 5px;
                font-size: 12.5px; font-weight: 600; padding: 6px 11px; border-radius: 8px;
                white-space: nowrap; font-variant-numeric: tabular-nums;
              }
              .avs-pill-open { background: rgba(22,163,74,0.10); color: #15803d; border: 1px solid rgba(22,163,74,0.35); }
              .avs-pill-inactive {
                background: rgba(107,114,128,0.08); color: #9ca3af;
                border: 1px solid rgba(107,114,128,0.25);
              }
              .avs-empty { font-size: 13px; color: var(--text-muted); font-style: italic; padding: 4px 0; }

              /* legacy legend classes (kept harmless) */
              .avs-legend { width: 14px; height: 14px; border-radius: 4px; display: inline-block; flex-shrink: 0; }

              @media (max-width: 560px) {
                .avs-day { flex-direction: column; gap: 10px; }
                .avs-day-head { width: auto; display: flex; align-items: baseline; gap: 10px; }
                .avs-day-date { margin-top: 0; }
                .avs-day-count { margin-top: 0; }
              }
            `}</style>
          </>
        )
      })()}

      {/* Modal + layout styles always mounted */}
      <style>{`
        .is-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
          display: flex; align-items: center; justify-content: center; padding: 24px;
        }
        .is-modal {
          background: white; border-radius: 16px; width: 100%; max-width: 460px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
          max-height: 90vh; display: flex; flex-direction: column;
        }
        .is-modal-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          padding: 20px 22px 16px; border-bottom: 1px solid var(--border); flex-shrink: 0;
        }
        .is-modal-title { font-size: 17px; font-weight: 700; color: var(--text-dark); margin: 0 0 6px; }
        .is-modal-close {
          background: none; border: none; cursor: pointer; color: var(--text-muted);
          padding: 2px; border-radius: 6px; display: flex; align-items: center;
        }
        .is-modal-close:hover { background: #f3f4f6; color: var(--text-dark); }
        .is-modal-body {
          padding: 20px 22px; display: flex; flex-direction: column; gap: 14px;
          overflow-y: auto; flex: 1; -webkit-overflow-scrolling: touch;
        }
        .is-modal-footer { padding: 14px 22px 20px; border-top: 1px solid var(--border); flex-shrink: 0; }

        /* ── Tabs scroll wrapper ── */
        .is-tabs-wrap {
          position: relative;
          margin-bottom: 24px;
        }
        .is-tabs-wrap .tabs {
          margin-bottom: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .is-tabs-wrap .tabs::-webkit-scrollbar { display: none; }
        @media (max-width: 640px) {
          .is-tabs-wrap::after {
            content: '';
            position: absolute;
            right: 0; top: 0;
            width: 40px; height: 100%;
            background: linear-gradient(to right, transparent, var(--bg, #f5f4ef) 80%);
            pointer-events: none;
            z-index: 1;
          }
        }

        /* ── Weekly Schedule toolbar ── */
        .is-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .is-toolbar-left {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .is-toolbar-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        /* ── Availability toggle ── */
        .is-avail-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 20px;
          border-radius: 12px;
          flex-wrap: wrap;
        }
        .is-avail-btn { padding: 10px 20px; flex-shrink: 0; }

        /* ── Mobile (≤640px) ── */
        @media (max-width: 640px) {
          .is-modal-overlay { padding: 12px; }
          .is-toolbar-right .is-sync-label { display: none; }
          .is-avail-toggle { flex-direction: column; align-items: flex-start; }
          .is-avail-btn { width: 100%; justify-content: center; }
        }

        /* ── Small mobile (≤480px) ── */
        @media (max-width: 480px) {
          .is-modal { max-width: 100%; border-radius: 14px; }
          .is-modal-header, .is-modal-body { padding: 14px 16px; }
          .is-modal-footer { padding: 12px 16px 16px; }
          .is-modal-footer .btn { width: 100%; justify-content: center; }
          .is-nav-btn { padding: 6px 10px; font-size: 12px; }
          .is-nav-today { min-width: 50px; }
        }
      `}</style>

      {/* ── Zoom Account tab ── */}
      {activeTab === 'zoom' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Zoom Account</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Connect your personal Zoom Pro account. Each interviewer uses their own account so multiple sessions can run simultaneously.
          </p>

          {zoomLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-muted)', fontSize: 14 }}>
              <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--primary-navy)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              Checking Zoom status…
            </div>
          ) : zoomConnected ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 20, background: 'rgba(72,187,120,0.06)', borderRadius: 12, border: '1px solid rgba(72,187,120,0.22)', marginBottom: 20 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(72,187,120,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckCircle size={22} style={{ color: 'var(--success)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>Zoom Account Connected</div>
                  {zoomUserEmail && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{zoomUserEmail}</div>
                  )}
                  {zoomConnectedAt && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      Connected on {new Date(zoomConnectedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <button
                className="btn btn-outline"
                onClick={async () => {
                  setZoomDisconnecting(true)
                  try {
                    await interviewerApi.disconnectZoom()
                    setZoomConnected(false)
                    setZoomUserEmail(null)
                    setZoomConnectedAt(null)
                    setToast({ type: 'success', text: 'Zoom account disconnected.' })
                  } catch {
                    setToast({ type: 'error', text: 'Failed to disconnect Zoom. Try again.' })
                  } finally {
                    setZoomDisconnecting(false)
                  }
                }}
                disabled={zoomDisconnecting}
                style={{ color: 'var(--danger)', borderColor: 'rgba(229,62,62,0.4)' }}
              >
                <LogOut size={16} />
                {zoomDisconnecting ? 'Disconnecting…' : 'Disconnect Zoom'}
              </button>
            </div>
          ) : (
            <div>
              <div style={{ padding: 18, background: 'rgba(229,62,62,0.04)', borderRadius: 10, border: '1px solid rgba(229,62,62,0.2)', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
                  You must connect a Zoom account before clients can book sessions with you. Your availability will not appear in search results until this is done.
                </p>
              </div>

              {/* Instructions */}
              <div style={{ padding: 18, background: 'rgba(66,153,225,0.04)', borderRadius: 10, border: '1px solid rgba(66,153,225,0.2)', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>How to connect your Zoom account</div>
                <ol style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
                  <li style={{ marginBottom: 6 }}>Click <strong>Connect with Zoom</strong> below — you'll be redirected to Zoom's login page.</li>
                  <li style={{ marginBottom: 6 }}>Sign in with the Zoom account you want to use for hosting interviews.</li>
                  <li style={{ marginBottom: 6 }}>Review the permissions and click <strong>Allow</strong> to grant access.</li>
                  <li>You'll be brought back here automatically once the connection is complete.</li>
                </ol>
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(66,153,225,0.15)', fontSize: 12, color: 'var(--text-muted)' }}>
                  <strong>Requirements:</strong> A Zoom Pro, Business, or higher plan is required to host unlimited-length meetings.
                </div>
              </div>

              <div style={{ padding: 18, background: 'rgba(45,55,72,0.03)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Why connect your own Zoom account?</div>
                <ul style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 18, margin: 0, lineHeight: 2 }}>
                  <li>Each interviewer hosts meetings independently — no scheduling conflicts</li>
                  <li>Recordings and transcripts go to your Zoom account automatically</li>
                  <li>You control your Zoom settings (background, recording, etc.)</li>
                </ul>
              </div>
              <button
                className="btn btn-primary"
                disabled={zoomConnecting}
                onClick={() => setShowZoomPlanModal(true)}
              >
                <Video size={18} />
                {zoomConnecting ? 'Redirecting…' : 'Connect with Zoom'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Zoom Plan Requirement Modal ── */}
      {showZoomPlanModal && (
        <div className="is-modal-overlay" onClick={() => !zoomConnecting && setShowZoomPlanModal(false)}>
          <div className="is-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="is-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(0,105,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Video size={20} style={{ color: '#0069ff' }} />
                </div>
                <div>
                  <h3 className="is-modal-title" style={{ marginBottom: 2 }}>Before you connect Zoom</h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                    Check that your account meets the requirements
                  </p>
                </div>
              </div>
              <button className="is-modal-close" onClick={() => !zoomConnecting && setShowZoomPlanModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="is-modal-body" style={{ gap: 0, padding: '16px 22px' }}>
              {/* Requirement list */}
              {[
                {
                  icon: <CheckCircle size={15} style={{ color: '#16a34a', flexShrink: 0, marginTop: 3 }} />,
                  title: 'Paid Zoom plan required',
                  desc: 'You must have a Zoom Pro, Business, or higher plan. Free (Basic) accounts cannot record to the cloud or generate transcripts.',
                },
                {
                  icon: <CheckCircle size={15} style={{ color: '#16a34a', flexShrink: 0, marginTop: 3 }} />,
                  title: 'Cloud recording must be enabled',
                  desc: 'We automatically record every session to your Zoom cloud storage so evaluations and transcripts are generated after the call.',
                },
                {
                  icon: <CheckCircle size={15} style={{ color: '#16a34a', flexShrink: 0, marginTop: 3 }} />,
                  title: 'Use your personal Zoom account',
                  desc: 'Connect the account you actually host meetings on. Each interviewer uses their own Zoom so sessions never conflict.',
                },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '12px 0', borderBottom: '1px solid var(--border)',
                }}>
                  {icon}
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}

              <div style={{
                marginTop: 16, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(234,179,8,0.07)', border: '1px solid rgba(234,179,8,0.35)',
                fontSize: 12, color: '#92400e', lineHeight: 1.5,
              }}>
                <strong>Important:</strong> If you connect a Free Zoom account, the connection will be rejected and you will be asked to upgrade before clients can book sessions with you.
              </div>
            </div>

            <div className="is-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 22px' }}>
              <button
                className="btn btn-outline"
                onClick={() => setShowZoomPlanModal(false)}
                disabled={zoomConnecting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={zoomConnecting}
                onClick={async () => {
                  setZoomConnecting(true)
                  setShowZoomPlanModal(false)
                  try {
                    const { authorization_url } = await interviewerApi.getZoomAuthUrl()
                    window.location.href = authorization_url
                  } catch {
                    setZoomConnecting(false)
                    setToast({ type: 'error', text: 'Could not initiate Zoom connection. Try again.' })
                  }
                }}
              >
                <Video size={16} />
                {zoomConnecting ? 'Redirecting…' : 'I have a paid plan — Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Availability tab ── */}
      {activeTab === 'availability' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Availability Toggle
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Toggle your availability to accept new session bookings.
          </p>

          {availabilityLocked ? (
            /* ── Admin-locked banner ── */
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 16,
              padding: 20, borderRadius: 12,
              background: 'rgba(220,38,38,0.05)',
              border: '1px solid rgba(220,38,38,0.25)',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(220,38,38,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>
                  Your account is currently unavailable.
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Please contact the admin to make it available again.
                  You cannot change your availability status while it is admin-locked.
                </div>
              </div>
            </div>
          ) : (
            /* ── Normal availability toggle ── */
            <div
              className="is-avail-toggle"
              style={{
                background: isAvailable ? 'rgba(72, 187, 120, 0.06)' : 'rgba(229, 62, 62, 0.06)',
                border: `1px solid ${isAvailable ? 'rgba(72, 187, 120, 0.2)' : 'rgba(229, 62, 62, 0.2)'}`,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  {isAvailable ? 'Available for Booking' : 'Not Available'}
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                  {isAvailable
                    ? 'Clients can book new sessions with you.'
                    : 'You are currently not accepting new bookings.'}
                </div>
              </div>
              <button
                className={`btn is-avail-btn ${isAvailable ? 'btn-danger' : 'btn-success'}`}
                onClick={handleToggleAvailability}
                disabled={togglingAvailability}
              >
                {togglingAvailability
                  ? 'Updating...'
                  : isAvailable
                    ? 'Go Unavailable'
                    : 'Go Available'}
              </button>
            </div>
          )}
        </div>
      )}
      {/* ── My Availability (native scheduler) tab ── */}
      {activeTab === 'my-availability' && (
        <>
          {/* Scheduler settings: timezone + buffers (accepting toggle lives on
              the "Availability" tab — it was a duplicate here) */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Availability Settings</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
              Set the timezone your hours are entered in and the buffer to keep around each session.
              To stop or resume taking new bookings, use the{' '}
              <button
                type="button"
                onClick={() => setActiveTab('availability')}
                style={{
                  background: 'none', border: 'none', padding: 0, font: 'inherit',
                  color: 'var(--primary-navy)', fontWeight: 600, cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Availability
              </button>{' '}
              tab.
            </p>

            {configQuery.isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-muted)', fontSize: 14 }}>
                <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--primary-navy)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                Loading your settings…
              </div>
            ) : configQuery.isError ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 16, background: 'rgba(229,62,62,0.05)', border: '1px solid rgba(229,62,62,0.25)', borderRadius: 10 }}>
                <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--danger)' }}>
                  {configQuery.error instanceof Error ? configQuery.error.message : 'Could not load your scheduler settings.'}
                  <button className="btn btn-outline" style={{ display: 'block', marginTop: 10, fontSize: 13 }} onClick={() => configQuery.refetch()}>
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* The accepting-bookings toggle used to live here. It was a
                    duplicate of the single control on the "Availability" tab, so
                    it was removed — that tab now drives both the profile flag and
                    sched_config.is_accepting_bookings. */}

                {/* Timezone */}
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select
                    className="form-input"
                    value={cfgTimezone}
                    onChange={(e) => setCfgTimezone(e.target.value)}
                  >
                    {timezoneOptions.map((tzOpt) => (
                      <option key={tzOpt.value} value={tzOpt.value}>{tzOpt.label}</option>
                    ))}
                  </select>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Your weekly hours and date overrides are interpreted in this timezone.
                  </p>
                </div>

                {/* Buffers */}
                <div className="ma-buffer-grid">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Buffer before session</label>
                    <select
                      className="form-input"
                      value={cfgBufferBefore}
                      onChange={(e) => setCfgBufferBefore(Number(e.target.value) as BufferMinutes)}
                    >
                      {BUFFER_OPTIONS.map((m) => (
                        <option key={m} value={m}>{m === 0 ? 'No buffer' : `${m} min`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Buffer after session</label>
                    <select
                      className="form-input"
                      value={cfgBufferAfter}
                      onChange={(e) => setCfgBufferAfter(Number(e.target.value) as BufferMinutes)}
                    >
                      {BUFFER_OPTIONS.map((m) => (
                        <option key={m} value={m}>{m === 0 ? 'No buffer' : `${m} min`}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  style={{ marginTop: 20 }}
                  onClick={handleSaveSchedulerConfig}
                  disabled={saveConfigMut.isPending}
                >
                  {saveConfigMut.isPending ? 'Saving…' : 'Save Settings'}
                </button>
              </>
            )}
          </div>

          {/* Availability extension: request bookings beyond the 45-day window */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Booking Window</h3>
            {(() => {
              const baseDays = extensionQuery.data?.current_window_days ?? configQuery.data?.booking_window_days ?? 45
              const extraDays = extensionQuery.data?.extra_days ?? 0
              const totalDays = extensionQuery.data?.effective_window_days ?? baseDays
              // Shown as MM-DD-YYYY; the API sends a calendar date (YYYY-MM-DD).
              const eff = formatCalendarDateUS(extensionQuery.data?.effective_until)
              return (
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
                  {extraDays > 0 ? (
                    <>
                      Clients can book you up to{' '}
                      <strong style={{ color: 'var(--text-dark)' }}>{totalDays} days</strong>{' '}
                      in advance — your standard {baseDays} plus{' '}
                      <strong style={{ color: 'var(--text-dark)' }}>{extraDays} approved extra</strong>
                      {eff ? <> (through {eff} today)</> : null}.
                    </>
                  ) : (
                    <>
                      Clients can book you up to{' '}
                      <strong style={{ color: 'var(--text-dark)' }}>{baseDays} days</strong>{' '}
                      in advance{eff ? <> (through {eff})</> : null}.
                    </>
                  )}{' '}
                  You can ask an administrator for extra days (60 days maximum in total).
                </p>
              )
            })()}

            {(() => {
              const status = extensionQuery.data?.latest_request?.status
              const effectiveUntil = formatCalendarDateUS(extensionQuery.data?.effective_until)
              const pending = status === 'pending'
              const approved = status === 'approved'
              const pillColor = pending
                ? 'rgba(214, 158, 46, 1)'
                : approved
                  ? 'rgba(56, 161, 105, 1)'
                  : 'var(--text-muted)'
              const pillBg = pending
                ? 'rgba(214, 158, 46, 0.1)'
                : approved
                  ? 'rgba(56, 161, 105, 0.1)'
                  : 'rgba(100, 116, 139, 0.1)'
              return (
                <div
                  className="is-avail-toggle"
                  style={{
                    background: 'rgba(66, 153, 225, 0.05)',
                    border: '1px solid rgba(66, 153, 225, 0.2)',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>Extended availability</div>
                      {status && (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            padding: '2px 10px',
                            borderRadius: 999,
                            color: pillColor,
                            background: pillBg,
                          }}
                        >
                          {status}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                      {approved
                        ? `+${extensionQuery.data?.extra_days ?? 0} day(s) added to your booking ` +
                          `window — currently through ${effectiveUntil}, and it moves forward daily.`
                        : pending
                          ? `Awaiting admin review: +${extensionQuery.data?.latest_request?.extra_days ?? 0} day(s) requested.`
                          : 'Ask for extra days on top of your standard booking window.'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Backing out is possible from BOTH states: withdraw a
                        request the admin has not decided, or hand back an
                        extension already in force. */}
                    {(pending || approved) && (
                      <button
                        className="btn btn-secondary is-avail-btn"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => {
                          setCancelExtKind(pending ? 'pending' : 'approved')
                          setShowCancelExtModal(true)
                        }}
                        disabled={cancelExtensionMut.isPending}
                      >
                        {pending ? 'Withdraw Request' : 'Cancel Extension'}
                      </button>
                    )}
                    <button
                      className="btn btn-primary is-avail-btn"
                      onClick={() => {
                        // Default to the smaller of 5 days or whatever headroom
                        // this interviewer still has under the 60-day ceiling.
                        const maxExtra = extensionQuery.data?.max_extra_days ?? 15
                        setExtExtraDays(Math.max(1, Math.min(5, maxExtra)))
                        setExtReason('')
                        setShowExtModal(true)
                      }}
                      disabled={pending}
                      title={pending ? 'A request is already pending' : undefined}
                    >
                      {pending ? 'Request Pending' : 'Request Extension'}
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Cancel / withdraw extended availability — confirm first, because it
              shrinks the window clients can book and cannot be undone without a
              fresh admin approval. */}
          {showCancelExtModal && (
            <div
              className="is-modal-overlay"
              onClick={() => setShowCancelExtModal(false)}
            >
              <div className="is-modal" onClick={(e) => e.stopPropagation()}>
                <div className="is-modal-header">
                  <div>
                    <h3 className="is-modal-title">
                      {cancelExtKind === 'pending'
                        ? 'Withdraw Extension Request'
                        : 'Cancel Extended Availability'}
                    </h3>
                  </div>
                </div>
                <div className="is-modal-body">
                  <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                    {cancelExtKind === 'pending' ? (
                      <>
                        Withdraw your pending request? The administrator will no
                        longer see it for review. You can submit a new request at
                        any time.
                      </>
                    ) : (
                      <>
                        Your bookable window will go back to the standard{' '}
                        <strong>
                          {extensionQuery.data?.current_window_days ?? 45} days
                        </strong>
                        , and clients will no longer be able to book the extended
                        dates. You would need a new administrator approval to open
                        them again.
                      </>
                    )}
                  </p>
                  {cancelExtKind === 'approved' && (
                    <p
                      style={{
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        lineHeight: 1.6,
                        marginTop: 10,
                      }}
                    >
                      Sessions already booked in that period are not cancelled —
                      they stay in your schedule.
                    </p>
                  )}
                </div>
                <div className="is-modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowCancelExtModal(false)}
                    disabled={cancelExtensionMut.isPending}
                  >
                    Keep It
                  </button>
                  <button
                    className="btn"
                    style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}
                    onClick={() => cancelExtensionMut.mutate()}
                    disabled={cancelExtensionMut.isPending}
                  >
                    {cancelExtensionMut.isPending
                      ? 'Cancelling…'
                      : cancelExtKind === 'pending'
                        ? 'Withdraw Request'
                        : 'Cancel Extension'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Availability-extension request modal */}
          {showExtModal && (
            <div
              className="is-modal-overlay"
              onClick={() => {
                setShowExtModal(false)
                setExtReason('')
              }}
            >
              <div className="is-modal" onClick={(e) => e.stopPropagation()}>
                <div className="is-modal-header">
                  <div>
                    <h3 className="is-modal-title">Request Extended Availability</h3>
                  </div>
                  <button
                    className="is-modal-close"
                    onClick={() => {
                      setShowExtModal(false)
                      setExtReason('')
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div className="is-modal-body">
                  {(() => {
                    const baseDays = extensionQuery.data?.current_window_days ?? 45
                    const maxExtra = extensionQuery.data?.max_extra_days ?? Math.max(0, 60 - baseDays)
                    const total = Math.min(baseDays + extExtraDays, 60)
                    return (
                      <div className="form-group">
                        <label className="form-label">How many extra days?</label>
                        <input
                          type="number"
                          className="form-input"
                          value={extExtraDays}
                          min={1}
                          max={maxExtra}
                          step={1}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10)
                            setExtExtraDays(Number.isNaN(n) ? 1 : n)
                          }}
                        />
                        {/* Live preview: the number is what is granted; the date
                            is only where that lands TODAY, and it moves daily. */}
                        <p style={{ fontSize: 13, marginTop: 8 }}>
                          Your booking window would go from{' '}
                          <strong>{baseDays} days</strong> to <strong>{total} days</strong> —
                          that is <strong>{addDaysISO(total)}</strong> as of today.
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                          This is a rolling extension: the extra days stay added as time
                          passes, so the date above moves forward with the calendar. You can
                          request up to <strong>{maxExtra}</strong> extra day(s) in total —
                          your standard {baseDays} plus {maxExtra} is the 60-day maximum. A new
                          approved request replaces any extension you already have.
                        </p>
                      </div>
                    )
                  })()}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Reason</label>
                    <textarea
                      className="form-input"
                      rows={4}
                      value={extReason}
                      maxLength={1000}
                      placeholder="Why do you need a longer booking window? (shown to the admin who reviews this)"
                      onChange={(e) => setExtReason(e.target.value)}
                    />
                  </div>
                </div>
                <div className="is-modal-footer">
                  <button
                    className="btn btn-outline"
                    onClick={() => {
                      setShowExtModal(false)
                      setExtReason('')
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={
                      requestExtensionMut.isPending ||
                      extExtraDays < 1 ||
                      extExtraDays > (extensionQuery.data?.max_extra_days ?? 60) ||
                      !extReason.trim()
                    }
                    onClick={() =>
                      requestExtensionMut.mutate({
                        extra_days: extExtraDays,
                        reason: extReason.trim(),
                      })
                    }
                  >
                    {requestExtensionMut.isPending ? 'Submitting…' : 'Submit Request'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Weekly recurring availability editor */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Recurring Weekly Availability</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
              Add one or more time windows for each weekday. Times are in{' '}
              <strong style={{ color: 'var(--text-dark)' }}>{cfgTimezone}</strong>. Days with no windows are treated as unavailable.
            </p>

            {rulesQuery.isLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading weekly hours…</div>
            ) : rulesQuery.isError ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 16, background: 'rgba(229,62,62,0.05)', border: '1px solid rgba(229,62,62,0.25)', borderRadius: 10 }}>
                <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--danger)' }}>
                  {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Could not load weekly hours.'}
                  <button className="btn btn-outline" style={{ display: 'block', marginTop: 10, fontSize: 13 }} onClick={() => rulesQuery.refetch()}>
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="ma-week">
                  {FULL_DAY_LABELS.map((label, day) => (
                    <div key={day} className="ma-day-row">
                      <div className="ma-day-name">{label}</div>
                      <div className="ma-windows">
                        {weeklyRows[day].length === 0 ? (
                          <span className="ma-unavailable">Unavailable</span>
                        ) : (
                          weeklyRows[day].map((win, idx) => (
                            <div key={idx} className="ma-window">
                              <input
                                type="time"
                                className="form-input ma-time"
                                value={win.start}
                                onChange={(e) => updateWindow(day, idx, 'start', e.target.value)}
                              />
                              <span className="ma-dash">–</span>
                              <input
                                type="time"
                                className="form-input ma-time"
                                value={win.end}
                                onChange={(e) => updateWindow(day, idx, 'end', e.target.value)}
                              />
                              <button
                                className="ma-icon-btn"
                                title="Remove window"
                                onClick={() => removeWindow(day, idx)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ))
                        )}
                        <button className="ma-add-btn" onClick={() => addWindow(day)}>
                          <Plus size={14} /> Add window
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="btn btn-primary"
                  style={{ marginTop: 20 }}
                  onClick={handleSaveWeeklyRules}
                  disabled={saveRulesMut.isPending}
                >
                  {saveRulesMut.isPending ? 'Saving…' : 'Save Weekly Hours'}
                </button>
              </>
            )}
          </div>

          {/* One-time date overrides */}
          <div className="card">
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Single Day Availability</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
              Override your weekly hours for a specific date — set custom hours or mark the whole day off.
            </p>

            {/* Add-override form */}
            <div className="ma-override-form">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={ovDate}
                  onChange={(e) => setOvDate(e.target.value)}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Type</label>
                <select
                  className="form-input"
                  value={ovType}
                  onChange={(e) => setOvType(e.target.value as 'custom' | 'blocked')}
                >
                  <option value="custom">Custom hours</option>
                  <option value="blocked">Day off (blocked)</option>
                </select>
              </div>

              {ovType === 'custom' && (
                <>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Start</label>
                    <input
                      type="time"
                      className="form-input"
                      value={ovStart}
                      onChange={(e) => setOvStart(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">End</label>
                    <input
                      type="time"
                      className="form-input"
                      value={ovEnd}
                      onChange={(e) => setOvEnd(e.target.value)}
                    />
                  </div>
                </>
              )}

              <button
                className="btn btn-primary ma-override-add"
                onClick={handleAddOverride}
                disabled={createOverrideMut.isPending}
              >
                <Plus size={15} />
                {createOverrideMut.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>

            {/* Existing overrides list */}
            <div style={{ marginTop: 24 }}>
              {overridesQuery.isLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading date overrides…</div>
              ) : overridesQuery.isError ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 16, background: 'rgba(229,62,62,0.05)', border: '1px solid rgba(229,62,62,0.25)', borderRadius: 10 }}>
                  <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: 'var(--danger)' }}>
                    {overridesQuery.error instanceof Error ? overridesQuery.error.message : 'Could not load date overrides.'}
                    <button className="btn btn-outline" style={{ display: 'block', marginTop: 10, fontSize: 13 }} onClick={() => overridesQuery.refetch()}>
                      Retry
                    </button>
                  </div>
                </div>
              ) : (overridesQuery.data ?? []).length === 0 ? (
                <div style={{ padding: '18px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, background: 'rgba(45,55,72,0.02)', border: '1px solid var(--border)', borderRadius: 10 }}>
                  No date overrides yet. Add one above to change a specific day.
                </div>
              ) : (
                <div className="ma-override-list">
                  {[...(overridesQuery.data ?? [])]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((ov) => {
                      const [y, mo, da] = ov.date.split('-').map(Number)
                      const dateObj = new Date(y, mo - 1, da)
                      return (
                        <div key={ov.id} className="ma-override-item">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            <span className={`ma-override-tag ${ov.type === 'blocked' ? 'ma-tag-blocked' : 'ma-tag-custom'}`}>
                              {ov.type === 'blocked' ? 'Day off' : 'Custom'}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)' }}>
                                {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                              {ov.type === 'custom' && (
                                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                                  {(ov.start_time ?? '').slice(0, 5)} – {(ov.end_time ?? '').slice(0, 5)}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            className="ma-icon-btn"
                            title="Remove override"
                            onClick={() => deleteOverrideMut.mutate(ov.id)}
                            disabled={deleteOverrideMut.isPending}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }

            .ma-buffer-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            }

            .ma-week { display: flex; flex-direction: column; }
            .ma-day-row {
              display: flex; align-items: flex-start; gap: 16px;
              padding: 14px 0; border-bottom: 1px solid var(--border);
            }
            .ma-day-row:last-child { border-bottom: none; }
            .ma-day-name {
              width: 96px; flex-shrink: 0; padding-top: 8px;
              font-size: 13px; font-weight: 600; color: var(--text-dark);
            }
            .ma-windows { display: flex; flex-direction: column; gap: 8px; flex: 1; align-items: flex-start; }
            .ma-window { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
            .ma-time { width: 150px; padding-left: 12px; padding-right: 10px; }
            .ma-dash { color: var(--text-muted); font-weight: 600; }
            .ma-unavailable { font-size: 13px; color: var(--text-muted); font-style: italic; padding: 6px 0; }
            .ma-icon-btn {
              display: inline-flex; align-items: center; justify-content: center;
              width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
              background: white; border: 1px solid var(--border); color: var(--danger);
              cursor: pointer; transition: all 0.15s;
            }
            .ma-icon-btn:hover:not(:disabled) { background: rgba(229,62,62,0.06); border-color: rgba(229,62,62,0.4); }
            .ma-icon-btn:disabled { opacity: 0.45; cursor: default; }
            .ma-add-btn {
              display: inline-flex; align-items: center; gap: 5px;
              background: none; border: 1px dashed var(--border);
              color: var(--primary-navy, #1e2432); border-radius: 8px;
              padding: 6px 12px; font-size: 12.5px; font-weight: 600; cursor: pointer;
              transition: all 0.15s;
            }
            .ma-add-btn:hover { background: rgba(45,55,72,0.04); border-color: #9ca3af; }

            .ma-override-form {
              display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
            }
            .ma-override-form .form-group { min-width: 150px; }
            .ma-override-add { flex-shrink: 0; }

            .ma-override-list { display: flex; flex-direction: column; gap: 8px; }
            .ma-override-item {
              display: flex; align-items: center; justify-content: space-between; gap: 12px;
              padding: 12px 16px; border: 1px solid var(--border); border-radius: 10px;
              background: #fafbfc;
            }
            .ma-override-tag {
              display: inline-flex; align-items: center; flex-shrink: 0;
              font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
              padding: 3px 10px; border-radius: 20px;
            }
            .ma-tag-custom { background: rgba(37,99,235,0.08); color: #1d4ed8; border: 1px solid rgba(37,99,235,0.2); }
            .ma-tag-blocked { background: rgba(107,114,128,0.1); color: #4b5563; border: 1px solid rgba(107,114,128,0.25); }

            @media (max-width: 640px) {
              .ma-buffer-grid { grid-template-columns: 1fr; }
              .ma-day-row { flex-direction: column; gap: 8px; }
              .ma-day-name { width: auto; padding-top: 0; }
              .ma-override-form { flex-direction: column; align-items: stretch; }
              .ma-override-form .form-group { min-width: 0; }
              .ma-override-add { width: 100%; justify-content: center; }
            }
          `}</style>
        </>
      )}

      {/* ── Preferences tab ── */}
      {activeTab === 'preferences' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Timezone and Language
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Set your timezone and preferred interview language.
          </p>

          <div className="form-group">
            <label className="form-label">Timezone</label>
            <select
              value={prefTimezone}
              onChange={(e) => setPrefTimezone(e.target.value)}
              className="form-input"
            >
              <option value="">Select your timezone</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">Greenwich Mean Time (GMT)</option>
              <option value="Europe/Paris">Central European Time (CET)</option>
              <option value="Asia/Kolkata">India Standard Time (IST)</option>
              <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              value={prefLanguage}
              onChange={(e) => setPrefLanguage(e.target.value)}
              className="form-input"
            >
              <option value="">Select interview language</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese (Mandarin)</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
              <option value="pt">Portuguese</option>
              <option value="hi">Hindi</option>
              <option value="ar">Arabic</option>
            </select>
          </div>

          <button
            className="btn btn-primary"
            disabled={savingPreferences || !preferencesChanged}
            style={{ marginTop: 8 }}
            onClick={handleSavePreferences}
          >
            {savingPreferences ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      )}
    </div>
  )
}
